/*
 * Design: Swiss Precision × 和の余白
 * サンクスページ — お問い合わせ完了を明確に伝える
 * フォーム送信後に自動遷移
 */

import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  ArrowLeft,
  Phone,
  Mail,
  Clock,
  FileText,
  MessageSquare,
  Sparkles,
} from "lucide-react";

export default function Thanks() {
  const [, setLocation] = useLocation();
  const [showConfetti, setShowConfetti] = useState(true);

  // Parse query params from URL for summary display
  const [summary, setSummary] = useState<{
    company?: string;
    plan?: string;
    set?: string;
  }>({});

  useEffect(() => {
    document.title = "お問い合わせ完了 | 81合同会社";

    // Parse URL search params
    const params = new URLSearchParams(window.location.search);
    setSummary({
      company: params.get("company") || undefined,
      plan: params.get("plan") || undefined,
      set: params.get("set") || undefined,
    });

    // Hide confetti after animation
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header bar */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[#F0F0F0]">
        <div className="container flex items-center justify-between h-16">
          <a
            href="/"
            className="flex items-center gap-2 text-[#1A1A1A] font-bold text-lg tracking-tight"
          >
            <span className="w-2.5 h-2.5 rounded-full bg-[#E03030]" />
            eightone
          </a>
          <button
            onClick={() => setLocation("/")}
            className="inline-flex items-center gap-2 text-sm text-[#666666] hover:text-[#E03030] transition-colors"
          >
            <ArrowLeft size={16} />
            トップへ戻る
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12 sm:py-20">
        <div className="w-full max-w-2xl">
          {/* Confetti particles */}
          {showConfetti && (
            <div className="fixed inset-0 pointer-events-none overflow-hidden z-40">
              {Array.from({ length: 24 }).map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 rounded-full"
                  style={{
                    backgroundColor: ["#E03030", "#1A1A1A", "#F9F9F9", "#FFD700", "#E03030"][i % 5],
                    left: `${Math.random() * 100}%`,
                    top: "-10px",
                  }}
                  initial={{ y: -20, opacity: 1, rotate: 0 }}
                  animate={{
                    y: window.innerHeight + 20,
                    opacity: 0,
                    rotate: Math.random() * 720 - 360,
                    x: Math.random() * 200 - 100,
                  }}
                  transition={{
                    duration: 2 + Math.random() * 1.5,
                    delay: Math.random() * 0.8,
                    ease: "easeOut",
                  }}
                />
              ))}
            </div>
          )}

          {/* Success card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center"
          >
            {/* Check icon with pulse */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
              className="relative w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-8"
            >
              <div className="absolute inset-0 bg-[#E03030]/10 rounded-full animate-ping" />
              <div className="absolute inset-0 bg-[#E03030] rounded-full flex items-center justify-center">
                <CheckCircle2 className="text-white" size={40} />
              </div>
            </motion.div>

            {/* Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1A1A1A] mb-4 tracking-tight"
            >
              お問い合わせ<span className="text-[#E03030]">完了</span>しました
            </motion.h1>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-sm sm:text-base text-[#555555] leading-relaxed mb-8 max-w-lg mx-auto"
            >
              お問い合わせいただきありがとうございます。<br />
              お問い合わせ内容を受け付けました。<br />
              <span className="font-medium text-[#1A1A1A]">2営業日以内</span>に担当者よりご連絡いたします。
            </motion.p>

            {/* Summary card (if params exist) */}
            {(summary.company || summary.plan || summary.set) && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="bg-[#F9F9F9] border border-[#E8E8E8] rounded-lg p-5 sm:p-6 mb-8 text-left max-w-md mx-auto"
              >
                <h3 className="text-xs font-bold text-[#999999] uppercase tracking-wider mb-3 flex items-center gap-2">
                  <FileText size={14} />
                  お問い合わせ内容
                </h3>
                <dl className="space-y-2 text-sm">
                  {summary.company && (
                    <div className="flex gap-3">
                      <dt className="text-[#999999] shrink-0 w-20">事業者名</dt>
                      <dd className="text-[#1A1A1A] font-medium">{decodeURIComponent(summary.company)}</dd>
                    </div>
                  )}
                  {summary.plan && (
                    <div className="flex gap-3">
                      <dt className="text-[#999999] shrink-0 w-20">プラン</dt>
                      <dd className="text-[#1A1A1A] font-medium">{decodeURIComponent(summary.plan)}</dd>
                    </div>
                  )}
                  {summary.set && (
                    <div className="flex gap-3">
                      <dt className="text-[#999999] shrink-0 w-20">セット</dt>
                      <dd className="text-[#1A1A1A] font-medium">{decodeURIComponent(summary.set)}</dd>
                    </div>
                  )}
                </dl>
              </motion.div>
            )}
          </motion.div>

          {/* Next steps */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="mt-4"
          >
            <h2 className="text-center text-xs font-bold text-[#999999] uppercase tracking-wider mb-6 flex items-center justify-center gap-2">
              <Sparkles size={14} />
              今後の流れ
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                {
                  step: "01",
                  icon: Mail,
                  title: "内容確認",
                  desc: "担当者がお問い合わせ内容を確認し、最適なご提案を準備します。",
                  time: "1営業日以内",
                },
                {
                  step: "02",
                  icon: MessageSquare,
                  title: "ヒアリング",
                  desc: "オンラインまたはお電話で、現場の課題を詳しくお伺いします。",
                  time: "約60分",
                },
                {
                  step: "03",
                  icon: FileText,
                  title: "ご提案",
                  desc: "課題に合わせたソリューションと効果試算をご提案します。",
                  time: "1週間以内",
                },
              ].map((item) => (
                <div
                  key={item.step}
                  className="bg-white border border-[#E8E8E8] rounded-lg p-5 text-center hover:border-[#E03030]/30 transition-colors"
                >
                  <div className="text-[10px] font-bold text-[#E03030] mb-2 tracking-widest">
                    STEP {item.step}
                  </div>
                  <div className="w-10 h-10 mx-auto mb-3 bg-[#F9F9F9] rounded-full flex items-center justify-center">
                    <item.icon size={18} className="text-[#1A1A1A]" />
                  </div>
                  <h3 className="text-sm font-bold text-[#1A1A1A] mb-2">{item.title}</h3>
                  <p className="text-xs text-[#777777] leading-relaxed mb-2">{item.desc}</p>
                  <span className="inline-flex items-center gap-1 text-[10px] text-[#999999] bg-[#F9F9F9] px-2 py-0.5 rounded-full">
                    <Clock size={10} />
                    {item.time}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Action buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <button
              onClick={() => setLocation("/")}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#E03030] text-white text-sm font-bold rounded hover:bg-[#c72828] transition-colors active:scale-[0.98] touch-manipulation"
            >
              <ArrowLeft size={16} />
              トップページへ戻る
            </button>
            <a
              href="tel:05050521301"
              className="inline-flex items-center gap-2 px-6 py-3 border border-[#E0E0E0] text-[#333333] text-sm font-medium rounded hover:border-[#E03030] hover:text-[#E03030] transition-colors touch-manipulation"
            >
              <Phone size={16} />
              お急ぎの方はお電話で
            </a>
          </motion.div>

          {/* Contact info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
            className="mt-8 text-center text-xs text-[#999999]"
          >
            <p>
              ご不明点がございましたら、お気軽にご連絡ください。
            </p>
            <p className="mt-1">
              <a href="tel:05050521301" className="hover:text-[#E03030] transition-colors">050-5052-1301</a>
              {" / "}
              <a href="mailto:81@eightone.co" className="hover:text-[#E03030] transition-colors">81@eightone.co</a>
            </p>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#F0F0F0] py-6">
        <div className="container text-center text-xs text-[#999999]">
          &copy; {new Date().getFullYear()} 81合同会社 All Rights Reserved.
        </div>
      </footer>
    </div>
  );
}
